/**
 * Dashboard colour theme preference — the persistence primitive behind
 * `DashboardThemeProvider`/`DashboardThemePicker` (IB-292). Structurally
 * identical to `themePreference.ts`/`accentPreference.ts` (design doc §3) —
 * the THIRD instance of the one persistence pattern already used for theme
 * (light/dark) and accent, not a new store.
 *
 * IB-292 (server-sync delta, 2026-08-27): `DashboardThemeProvider` now
 * hydrates from / best-effort syncs to the real per-user preference endpoint
 * (`GET/PUT/DELETE /api/v1/me/preferences/dashboardTheme`,
 * `api/userPreferences.ts`) — this module's `read`/`write` shape is
 * UNCHANGED (as designed) and stays the FAST, synchronous, per-browser
 * `localStorage` primitive `DashboardThemeProvider` reads for first paint;
 * the server round trip only ever overrides it post-hydration when the two
 * disagree. RAID R-31 (the "not yet cross-device" finding) is CLOSED by this
 * delta — the choice now genuinely follows the user across devices.
 */
import { isDashboardThemeId, type DashboardThemeId } from './dashboardThemePresets'

export type { DashboardThemeId } from './dashboardThemePresets'

export const DASHBOARD_THEME_STORAGE_KEY = 'iw.dashboard-theme-preference'

/**
 * Reads the persisted preference. Never throws — a storage failure (private
 * browsing, disabled storage, quota), an unset value, corrupt storage, OR a
 * previously-valid id that has since been removed/renamed from the CURRENT
 * `DASHBOARD_THEME_PRESETS` registry all fall back to `null` ("Platform
 * Default" — AC7/AC8).
 */
export function readDashboardThemePreference(): DashboardThemeId | null {
  try {
    const raw = window.localStorage.getItem(DASHBOARD_THEME_STORAGE_KEY)
    return isDashboardThemeId(raw) ? raw : null
  } catch {
    return null
  }
}

/**
 * Persists the preference. `null` clears storage (reset to Platform
 * Default). Silently no-ops on storage failure (state still applies for the
 * session).
 */
export function writeDashboardThemePreference(id: DashboardThemeId | null): void {
  try {
    if (id === null) {
      window.localStorage.removeItem(DASHBOARD_THEME_STORAGE_KEY)
    } else {
      window.localStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, id)
    }
  } catch {
    // Storage unavailable — the in-memory/React state still reflects the choice this session.
  }
}
